import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { collection, query, getDocs, where, orderBy, limit, doc, updateDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import LightModeLoanCard from './LightModeLoanCard';
import { BarsArrowUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, FunnelIcon, StarIcon } from '@heroicons/react/20/solid';
import { BankManagement } from './BankManagement';
import { PreLenderCard, InTalksCard, ClosedDealCard, PrequalificationCard } from './LoanCards';
import { LoanStatuses } from '../constants/statuses';
import { format } from 'date-fns';
import { Menu } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import { PaperClipIcon } from '@heroicons/react/20/solid';
import LenderMatchCard from './LenderMatchCard';
import CreateLenderMatchModal from './CreateLenderMatchModal';
import PrequalificationTabDetails from './PrequalificationTabDetails';


// Define separate tabs for each loan type
const acquisitionTabs = [
  { name: 'Recent Activity', section: 'recentActivity' },
  { name: 'Pre-Lender', section: 'preLender' },
  { name: 'In Talks', section: 'inTalks' },
  { name: 'Closed Deals', section: 'closedDeals' },
  { name: 'Bank Management', section: 'bankManagement' },
];

const workingCapitalTabs = [
  { name: 'Recent Activity', section: 'recentActivity' },
  { name: 'New Applications', section: LoanStatuses.NEW_APPLICATION },
  { name: 'Submitted', section: LoanStatuses.SUBMITTED },
  { name: 'Prequalified', section: LoanStatuses.PREQUALIFIED_FOR },
  { name: 'Document Review', section: LoanStatuses.DOCUMENT_CLARIFICATION },
  { name: 'Underwriting', section: LoanStatuses.UNDERWRITING },
  { name: 'Funded', section: LoanStatuses.FUNDED },
  { name: 'Bank Management', section: 'bankManagement' },
];

const loanPurposeFilters = [
  { value: 'all', label: 'All Purposes' },
  { value: 'Acquisition', label: 'Acquisition' },
  { value: 'workingCapital', label: 'Working Capital' },
  { value: 'workingCapital-startup', label: 'Working Capital (Startup)' },
  { value: 'workingCapital-nonsba', label: 'Working Capital (Non-SBA)' },
];

const LOAN_TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'Acquisition', label: 'Acquisition' },
  { value: 'workingCapital', label: 'Working Capital' },
  { value: 'calls', label: 'Calls' },
  { value: 'starred', label: 'Starred' },
  { value: 'lenderMatch', label: 'Lender Match' },
];

const callsTabs = [
  { name: 'Upcoming Calls', section: 'upcomingCalls' },
  { name: 'Scheduled', section: 'scheduledCalls' },
];

// Add this with other tab definitions
const lenderMatchTabs = [
  { name: 'Pending Matches', section: 'pendingMatches' },
  { name: 'Matched', section: 'matched' },
  { name: 'Declined', section: 'declined' },
];

// Add this constant near the top with other constants
const LOAN_STAGES = [
  { id: 'underwriting', label: 'Underwriting', color: 'purple' },
  { id: 'prequalified', label: 'Prequalified', color: 'orange' },
  { id: 'documentReview', label: 'Document Review', color: 'orange' },
  { id: 'awaitingDocs', label: 'Awaiting Docs', color: 'blue' },
  { id: 'docsUploaded', label: 'Docs Ready', color: 'yellow' },
  { id: 'funded', label: 'Funded', color: 'green' },
];

// *** Updated Status Checklists Definition ***
const statusChecklists = {
  [LoanStatuses.NEW_APPLICATION]: [
    'Call to explain process',
  ],
  [LoanStatuses.SUBMITTED]: [ // Checklist items for when the status *is* SUBMITTED
    'Send initial package to bank'
  ],
  // Removed SUBMITTED_DocsUploaded key
  [LoanStatuses.PREQUALIFIED_FOR]: [
    'Send consulting agreement',
    'Signed consulting agreement',
  ],
  [LoanStatuses.TERM_SHEET_EXTENDED]: [
    'Send consulting agreement',
    'Signed consulting agreement',
  ],
  [LoanStatuses.DOCUMENT_CLARIFICATION]: [
    'Review received documents',
    'Send updated package to bank',
  ],
  [LoanStatuses.CLOSING]: [
    'Send closing package to bank',
  ],
   // Add other statuses if they need checklists
   [LoanStatuses.UNDERWRITING]: [], // Example: No checklist needed
   [LoanStatuses.REJECTED]: [],
   [LoanStatuses.FUNDED]: [],
};
// *** End Status Checklists Definition ***

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Update the status mapping
const statuses = {
  'New Application': 'text-blue-700 bg-blue-50 ring-blue-600/20',
  'Submitted': 'text-yellow-700 bg-yellow-50 ring-yellow-600/20',
  'Funded': 'text-green-700 bg-green-50 ring-green-600/20',
  'Pre-lender': 'text-purple-700 bg-purple-50 ring-purple-600/20',
  'Document Review': 'text-orange-700 bg-orange-50 ring-orange-600/20',
  'Prequalified': 'text-orange-700 bg-orange-50 ring-orange-600/20',
  'Acquisition': 'text-emerald-700 bg-emerald-50 ring-emerald-600/20',  // Add this line
  'Underwriting': 'text-purple-700 bg-purple-50 ring-purple-600/20',
};

const getDaysSince = (date) => {
  if (!date) return 0;
  // Ensure Firestore Timestamp or JS Date object
  const targetDate = date?.toDate ? date.toDate() : new Date(date);
  if (isNaN(targetDate.getTime())) {
   // console.warn("Invalid date passed to getDaysSince:", date);
    return 0; // Return 0 if the date is invalid
  }
  const today = new Date();
  const diffTime = Math.abs(today - targetDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Add this near the top with other constants
const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Define getFirstChar *before* it's used in renderSection
const getFirstChar = (loan) => {
  const name = loan.businessName || `${loan.firstName} ${loan.lastName}`.trim();
  const char = name.charAt(0).toUpperCase();
  return /[A-Z]/.test(char) ? char : '#';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [recentUsers, setRecentUsers] = useState([]);
  const [preLenderLoans, setPreLenderLoans] = useState([]);
  const [inTalksLoans, setInTalksLoans] = useState([]);
  const [closedDeals, setClosedDeals] = useState([]);
  const [prequalifications, setPrequalifications] = useState([]);
  const [activeSection, setActiveSection] = useState('recentActivity');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLoanType, setSelectedLoanType] = useState('Acquisition');
  const [loans, setLoans] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [displayStats, setDisplayStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [lenderMatches, setLenderMatches] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  // Update the isToday function to handle UTC dates
  const isToday = (date1, date2) => {
    if (!date1 || !date2) return false;
    
    // Ensure we're working with Date objects
    const d1 = date1 instanceof Date ? date1 : date1.toDate();
    const d2 = date2 instanceof Date ? date2 : date2.toDate();
    
    // Compare year, month, and day
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Get current tabs based on loan type
  const getCurrentTabs = () => {
    switch (selectedLoanType) {
      case 'Acquisition':
        return acquisitionTabs;
      case 'workingCapital':
        return workingCapitalTabs;
      case 'calls':
        return callsTabs;
      case 'lenderMatch':
        return lenderMatchTabs;
      case 'starred':
        return [];
      default:
        return acquisitionTabs;
    }
  };

  // Update handleLoanTypeChange to include prequalification
  const handleLoanTypeChange = (type) => {
    setSelectedLoanType(type);
    setActiveSection(type === 'prequalification' ? 'prequalifications' : 'recentActivity');
  };

  // Modify the fetch functions to filter by loan type
  const fetchLoans = async (status) => {
    try {
      const loansRef = collection(db, 'loanApplications');
      let q;

      if (selectedLoanType === 'workingCapital') {
        q = query(
          loansRef,
          where('loanPurpose', '==', 'workingCapital'),
          where('loanStatus', '==', status)
        );
      } else {
        q = query(
          loansRef,
          where('loanPurpose', '==', 'Acquisition'),
          where('status', '==', status)
        );
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching loans:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchRecentUsers = async () => {
      try {
        const usersRef = collection(db, 'loanApplications');
        const q = query(usersRef, orderBy('createdAt', 'desc'), limit(5));
        const querySnapshot = await getDocs(q);
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRecentUsers(usersData);
      } catch (error) {
        console.error('Error fetching recent users:', error);
      }
    };

    const fetchPreLenderLoans = async () => {
      try {
        const loansRef = collection(db, 'loanApplications');
        const q = query(loansRef, where('status', '==', 'Pre-lender'));
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPreLenderLoans(loansData);
      } catch (error) {
        console.error('Error fetching pre-lender loans:', error);
      }
    };

    const fetchInTalksLoans = async () => {
      try {
        const loansRef = collection(db, 'loanApplications');
        const q = query(loansRef, where('status', 'in', ['in-talks', 'under_review']));
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setInTalksLoans(loansData);
      } catch (error) {
        console.error('Error fetching in-talks loans:', error);
      }
    };

    const fetchClosedDeals = async () => {
      try {
        const loansRef = collection(db, 'loanApplications');
        const q = query(loansRef, where('status', '==', 'closed'));
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClosedDeals(loansData);
      } catch (error) {
        console.error('Error fetching closed deals:', error);
      }
    };

    const fetchPrequalifications = async () => {
      console.log('Fetching prequalifications...');
      try {
        // First get all brokers
        const brokersRef = collection(db, 'users');  // Changed from 'brokers' to 'users'
        const brokersSnapshot = await getDocs(brokersRef);
        const brokersMap = {};
        
        brokersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          // Only add users who are brokers
          if (userData.role === 'broker') {
            brokersMap[doc.id] = {
              id: doc.id,
              name: userData.name || userData.businessName || userData.companyName || 'Unknown Broker',
              email: userData.email,
            };
          }
        });

        console.log('Brokers map:', brokersMap);

        // Then get prequalifications
        const prequalRef = collection(db, 'prequalificationApplications');
        const prequalSnapshot = await getDocs(prequalRef);
        
        const prequalificationsWithBrokers = prequalSnapshot.docs.map(doc => {
          const data = doc.data();
          const brokerId = data.brokerId || data.userId;
          const brokerInfo = brokersMap[brokerId];
          
          console.log(`Mapping prequalification for broker ${brokerId}:`, brokerInfo);

          return {
            id: doc.id,
            ...data,
            brokerName: brokerInfo?.name || 'Unknown Broker',
            brokerEmail: brokerInfo?.email || 'No email',
            broker: brokerInfo
          };
        });

        console.log('Final prequalifications with brokers:', prequalificationsWithBrokers);
        setPrequalifications(prequalificationsWithBrokers);
      } catch (error) {
        console.error('Error fetching prequalifications:', error);
        console.error('Error details:', error.message);
      }
    };

    fetchRecentUsers();
    fetchPreLenderLoans();
    fetchInTalksLoans();
    fetchClosedDeals();
    fetchPrequalifications();
  }, [selectedLoanType]);

  // Modify the fetchAllLoans function
  const fetchAllLoans = async () => {
    try {
      const loansRef = collection(db, 'loanApplications');
      let q;
      
      if (selectedLoanType === 'starred') {
        // Query for starred applications
        q = query(
          loansRef,
          where('starred', '==', true)
        );
        
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLoans(loansData);
      } else if (selectedLoanType === 'calls') {
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        q = query(loansRef);
        
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const filteredLoans = loansData.filter(loan => {
          const loanDate = loan.displayDate?.toDate?.() || loan.createdAt?.toDate?.() || new Date(loan.createdAt);
          const loanLocalDate = new Date(loanDate).toLocaleDateString();
          const selectedLocalDate = startOfDay.toLocaleDateString();
          
          return loanLocalDate === selectedLocalDate;
        });

        setLoans(filteredLoans);
      } else {
        // Original query for non-calls views
        q = query(
          loansRef,
          where('loanPurpose', '==', selectedLoanType === 'workingCapital' ? 'workingCapital' : 'Acquisition')
        );
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLoans(loansData);
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      setLoans([]);
    }
  };

  // Use it in the useEffect
  useEffect(() => {
    fetchAllLoans();
  }, [selectedDate, selectedLoanType]);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        navigate('/login');
      })
      .catch((error) => {
        console.error('Sign out error:', error);
      });
  };

  const handleCardClick = (loan) => {
    console.log('Card clicked:', loan);
    if (activeSection === 'prequalifications') {
      console.log('Navigating to prequalification details:', `/prequalification-details/${loan.id}`);
      navigate(`/prequalification-details/${loan.id}`);
    } else {
      navigate(`/loan-details/${loan.id}`);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  const changeDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (direction === 'next') {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
  };

  const navigateToLoanDate = (loan) => {
    let displayDate;
    
    try {
      if (loan.called) {
        // For called loans, try followUpDate first, then displayDate
        if (loan.followUpDate?.toDate) {
          displayDate = loan.followUpDate.toDate();
        } else if (loan.displayDate?.toDate) {
          displayDate = loan.displayDate.toDate();
        } else if (loan.followUpDate) {
          displayDate = new Date(loan.followUpDate);
        } else if (loan.displayDate) {
          displayDate = new Date(loan.displayDate);
        }
      } else {
        // For uncalled loans, try createdAt
        if (loan.createdAt?.toDate) {
          displayDate = loan.createdAt.toDate();
        } else if (loan.createdAt) {
          displayDate = new Date(loan.createdAt);
        }
      }
      
      // If we couldn't get a valid date, use today's date
      if (!displayDate || isNaN(displayDate.getTime())) {
        displayDate = new Date();
        console.warn('Using current date as fallback for loan:', loan.id);
      }
      
      setSelectedDate(displayDate);
    } catch (error) {
      console.error('Error processing date for loan:', loan.id, error);
      // Fallback to current date if there's an error
      setSelectedDate(new Date());
    }
  };

  useEffect(() => {
    const searchAcrossLoans = async () => {
      if (!searchTerm) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const searchTermLower = searchTerm.toLowerCase();

      try {
        const loansRef = collection(db, 'loanApplications');
        const querySnapshot = await getDocs(loansRef);
        const allLoans = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter based on search term
        const filteredLoans = allLoans.filter(loan => {
          // Check business name
          const businessNameMatch = loan.businessName?.toLowerCase().includes(searchTermLower);
          
          // Check first and last name
          const fullNameMatch = `${loan.firstName} ${loan.lastName}`.toLowerCase().includes(searchTermLower);
          
          // Check primary email
          const emailMatch = loan.email?.toLowerCase().includes(searchTermLower);
          
          // Check emails array
          const emailsArrayMatch = Array.isArray(loan.emails) && 
            loan.emails.some(email => email.toLowerCase().includes(searchTermLower));

          return businessNameMatch || fullNameMatch || emailMatch || emailsArrayMatch;
        });

        setSearchResults(filteredLoans);
      } catch (error) {
        console.error('Error searching loans:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce the search to avoid too many requests
    const timeoutId = setTimeout(() => {
      searchAcrossLoans();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const getDaysInStage = (loan) => {
    // Mirror the logic from loandetails.js
    if (!loan) return 0;
    const status = loan.loanStatus || LoanStatuses.NEW_APPLICATION;
    let relevantDate;

    // Use specific timestamps if available, otherwise fall back
    switch(status) {
        case LoanStatuses.SUBMITTED:
            relevantDate = loan.submittedAt; // Use the specific submitted timestamp
            break;
        case LoanStatuses.UNDERWRITING:
            relevantDate = loan.underwritingAt || loan.submittedAt;
            break;
        case LoanStatuses.PREQUALIFIED_FOR:
             // Check for variations in timestamp field names
             relevantDate = loan.prequalified_forAt || loan.prequalifiedAt || loan.submittedAt;
            break;
        case LoanStatuses.TERM_SHEET_EXTENDED:
            relevantDate = loan.term_sheet_extendedAt || loan.submittedAt;
            break;
        case LoanStatuses.DOCUMENT_CLARIFICATION:
            relevantDate = loan.document_clarificationAt || loan.submittedAt;
            break;
        case LoanStatuses.CLOSING:
             relevantDate = loan.closingAt || loan.submittedAt;
             break;
        case LoanStatuses.FUNDED:
            relevantDate = loan.fundedAt || loan.submittedAt;
            break;
        case LoanStatuses.REJECTED:
             relevantDate = loan.rejectedAt || loan.createdAt;
             break;
        case LoanStatuses.NEW_APPLICATION:
        default:
            // Use specific status timestamp (lowercase_with_underscores) or createdAt
            const statusKey = status.toLowerCase().replace(/\s+/g, '_') + 'At';
            relevantDate = loan[statusKey] || loan.createdAt;
            break;
    }

    // Final fallback to createdAt if no relevant date found
    return getDaysSince(relevantDate || loan.createdAt);
  };

  const getCurrentChecklist = (loan) => {
    if (!loan) return [];
    // Directly use the loanStatus to get the checklist
    return statusChecklists[loan.loanStatus || LoanStatuses.NEW_APPLICATION] || [];
  };

  const isChecklistComplete = (loan) => {
    const checklist = getCurrentChecklist(loan);
    if (!checklist || checklist.length === 0) {
      return true; // No checklist means it's complete
    }
    const checklistState = loan.statusChecklistState || {};
    return checklist.every(item => checklistState[item] === true);
  };

  const getAlertMessage = (loan) => {
    // Mirror the logic from loandetails.js
    if (!loan || !loan.loanStatus) return null;

    const loanStatus = loan.loanStatus;
    const daysInStatus = getDaysInStage(loan); // Use updated calculation
    const checklistState = loan.statusChecklistState || {};
    const submittedAt = loan.submittedAt; // Get the submission timestamp

    // Handle SUBMITTED status
    if (loanStatus === LoanStatuses.SUBMITTED) {
      if (checklistState['Send initial package to bank'] === true) {
        const daysSinceSubmission = submittedAt ? getDaysSince(submittedAt) : 0;
        if (daysSinceSubmission >= 4) return "Follow up with bank (Day 4+)";
        if (daysSinceSubmission >= 2) return "Follow up with bank (Day 2)";
      }
      return null;
    }

    // Handle other explicit statuses
    switch (loanStatus) {
      case LoanStatuses.NEW_APPLICATION:
        if (daysInStatus >= 5) return "Follow up with client (Day 5+)";
        if (daysInStatus >= 2) return "Follow up with client (Day 2)";
        return null;
      case LoanStatuses.PREQUALIFIED_FOR:
      case LoanStatuses.TERM_SHEET_EXTENDED:
        if (daysInStatus >= 4) return "Follow up (Day 4+)";
        return null;
      case LoanStatuses.DOCUMENT_CLARIFICATION:
        if (daysInStatus >= 4) return "Follow up with client (Day 4+)";
        if (daysInStatus >= 2) return "Follow up with client (Day 2)";
        return null;
      case LoanStatuses.CLOSING:
        if (daysInStatus >= 5) return "Follow up with bank (Day 5+)";
        return null;
      default:
        return null;
    }
  };

  const getLoanStatus = (loan) => {
    // This function determines the 'urgency' level for sorting/display
    if (!loan) return 'onTrack';
    const loanStatus = loan.loanStatus || LoanStatuses.NEW_APPLICATION;
    const alertMessage = getAlertMessage(loan);
    const checklistComplete = isChecklistComplete(loan);

    // This logic primarily applies to the 'starred' view for prioritization
    if (selectedLoanType === 'starred') {
        // If checklist is not complete for the *current* status, it's urgent
        if (!checklistComplete) {
            return 'urgent';
        }
        // If checklist is complete, but a time-based alert is active, needs follow-up
        if (alertMessage) {
            return 'followUpNeeded';
        }
        // Otherwise, it's on track for its current status
        return 'onTrack';
    }

    // Fallback for non-starred sections (uses simpler time-based warning)
    const days = getDaysInStage(loan);
    if (loanStatus === LoanStatuses.UNDERWRITING && days > 20) return 'warning';
    if (loanStatus !== LoanStatuses.FUNDED && loanStatus !== LoanStatuses.REJECTED && loanStatus !== LoanStatuses.UNDERWRITING && days > 7) return 'warning';
    return 'onTrack'; // Default for non-starred view if no warning
  };

  const getNextTask = (loan) => {
    // Mirror the logic from loandetails.js for consistency
    if (!loan) return { text: "No Data", status: "text-gray-700 bg-gray-50 ring-gray-600/20" };

    const days = getDaysInStage(loan);
    const calculatedUrgency = getLoanStatus(loan); // 'urgent', 'followUpNeeded', 'onTrack' (for starred) or 'warning', 'onTrack' (for others)
    const loanStatus = loan.loanStatus || LoanStatuses.NEW_APPLICATION;
    const checklist = getCurrentChecklist(loan);
    const checklistState = loan.statusChecklistState || {};
    const alertMessage = getAlertMessage(loan);
    const submittedAt = loan.submittedAt;

    if (selectedLoanType === 'starred') {
      // If Urgent (Checklist incomplete)
      if (calculatedUrgency === 'urgent') {
        const firstUncheckedItem = checklist.find(item => !checklistState[item]);
        let taskText = firstUncheckedItem ? `Action needed: ${firstUncheckedItem}` : 'Urgent: Review Checklist';
        return { text: taskText, status: "text-red-700 bg-red-50 ring-red-600/20" };
      }

      // If Follow Up Needed (Time-based alert is active)
      if (calculatedUrgency === 'followUpNeeded') {
        let taskText = alertMessage || 'Follow-up Needed';
        // Add context based on status
        if (loanStatus === LoanStatuses.NEW_APPLICATION) {
          taskText = `Awaiting submission - ${taskText} • ${days} day${days === 1 ? '' : 's'}`;
        } else if (loanStatus === LoanStatuses.SUBMITTED && checklistState['Send initial package to bank'] === true) {
           const daysSinceSubmission = submittedAt ? getDaysSince(submittedAt) : days;
           taskText = `Package Sent - ${taskText} • ${daysSinceSubmission} day${daysSinceSubmission === 1 ? '' : 's'}`;
        } else {
           taskText += ` • ${days} day${days === 1 ? '' : 's'}`; // Add day count for other statuses
        }
        return { text: taskText, status: "text-yellow-700 bg-yellow-50 ring-yellow-600/20" };
      }

      // If On Track (Checklist complete, no alert)
      if (calculatedUrgency === 'onTrack') {
         if (loanStatus === LoanStatuses.NEW_APPLICATION && checklistState['Call to explain process'] === true) {
            return { text: `Awaiting Documents • ${days} day${days === 1 ? '' : 's'}`, status: "text-blue-700 bg-blue-50 ring-blue-600/20" };
         }
         if (loanStatus === LoanStatuses.SUBMITTED && checklistState['Send initial package to bank'] === true) {
            const daysAwaitingFeedback = submittedAt ? getDaysSince(submittedAt) : days;
            return { text: `Awaiting Bank Feedback • ${daysAwaitingFeedback} day${daysAwaitingFeedback === 1 ? '' : 's'}`, status: statuses['Submitted'] || "text-yellow-700 bg-yellow-50 ring-yellow-600/20" };
         }
         // Other 'onTrack' states will fall through to the general logic below
      }
    }

    // General Logic (also applies to 'onTrack' starred loans not caught above)
    // Provides a general task based on the current status and adds day count
    const statusText = loanStatus.replace(/_/g, ' '); // Make status readable
    let taskDescription = `Status: ${statusText}`;
    let taskStatusStyle = statuses[loanStatus] || "text-gray-700 bg-gray-50 ring-gray-600/20"; // Default style
    const isComplete = isChecklistComplete(loan); // Call function here

    switch (loanStatus) {
        case LoanStatuses.FUNDED:
            taskDescription = `Funded`;
            break;
        case LoanStatuses.REJECTED:
            taskDescription = `Rejected`;
             break;
        case LoanStatuses.NEW_APPLICATION:
            taskDescription = `Awaiting Documents • ${days} day${days === 1 ? '' : 's'}`;
            break;
        case LoanStatuses.SUBMITTED:
             // If checklist item 'Send...' is not checked, that's the implied next task
             if (!checklistState['Send initial package to bank']) {
                taskDescription = `Submitted - Send Package • ${days} day${days === 1 ? '' : 's'}`;
             } else {
                taskDescription = `Submitted - Awaiting Feedback • ${days} day${days === 1 ? '' : 's'}`;
             }
            break;
        // Add other specific descriptions if needed
        default:
            taskDescription = `${statusText} • ${days} day${days === 1 ? '' : 's'}`;
            break;
    }

     // Apply warning style from getLoanStatus if applicable (for non-starred view)
     if (selectedLoanType !== 'starred' && calculatedUrgency === 'warning') {
        taskStatusStyle = "text-orange-700 bg-orange-50 ring-orange-600/20"; // Example warning style
     } else if (loanStatus === LoanStatuses.FUNDED || loanStatus === LoanStatuses.REJECTED || isComplete) { // Use the isComplete variable
         // Use green 'onTrack' style if funded, rejected, or checklist complete without alerts
         taskStatusStyle = "text-green-700 bg-green-50 ring-green-600/20";
     } else {
         // Use the status-specific style otherwise
         taskStatusStyle = statuses[loanStatus] || taskStatusStyle;
     }

    return { text: taskDescription, status: taskStatusStyle };
  };

  const getLoanStage = (loan) => {
    const status = loan?.loanStatus;
    if (!status) return 'awaitingDocs'; // Default stage

    switch (status) {
      case LoanStatuses.FUNDED: return 'funded';
      case LoanStatuses.UNDERWRITING: return 'underwriting';
      case LoanStatuses.PREQUALIFIED_FOR:
      case LoanStatuses.TERM_SHEET_EXTENDED: return 'prequalified';
      case LoanStatuses.DOCUMENT_CLARIFICATION: return 'documentReview';
      case LoanStatuses.SUBMITTED: return 'submitted'; // Renamed stage from 'docsUploaded'
      case LoanStatuses.NEW_APPLICATION: return 'awaitingDocs';
      case LoanStatuses.CLOSING: return 'closing'; // Added closing stage mapping
      case LoanStatuses.REJECTED: return 'rejected'; // Added rejected stage mapping
      default: return 'awaitingDocs'; // Fallback
    }
  };

  const QuickStatsBar = ({ loans, onFilterClick, activeFilter }) => {
    // Recalculate counts based on the final getLoanStatus logic
    const urgentCount = loans.filter(loan => getLoanStatus(loan) === 'urgent').length;
    const followUpNeededCount = loans.filter(loan => getLoanStatus(loan) === 'followUpNeeded').length;
    const onTrackCount = loans.filter(loan => getLoanStatus(loan) === 'onTrack').length;

    const stats = {
      urgent: {
        label: 'Urgent Action Needed',
        count: urgentCount,
        description: 'Checklist incomplete',
        color: 'red',
        filterValue: 'urgent',
      },
      followUpNeeded: {
        label: 'Follow Up Needed',
        count: followUpNeededCount,
        description: 'Follow-up due',
        color: 'yellow',
        filterValue: 'followUpNeeded',
      },
      onTrack: {
        label: 'On Track',
        count: onTrackCount,
        description: 'Regular progress',
        color: 'green',
        filterValue: 'onTrack',
      },
    };

    return (
      <div className="mb-6 grid grid-cols-3 gap-4">
        {Object.entries(stats).map(([key, stat]) => (
          <button
            key={key}
            onClick={() => onFilterClick(activeFilter === stat.filterValue ? null : stat.filterValue)}
            className={`bg-white rounded-lg shadow p-4 transition-all hover:shadow-lg relative ${
              activeFilter === stat.filterValue ? 'ring-2 ring-indigo-600' : ''
            }`}
          >
            <div className="text-sm font-medium text-gray-500">{stat.label}</div>
            <div className={`mt-1 text-2xl font-semibold text-${stat.color}-600`}>
              {stat.count}
            </div>
            <div className="text-xs text-gray-400 mt-1">{stat.description}</div>
            {activeFilter === stat.filterValue && (
              <div className="absolute -top-2 -right-2 h-4 w-4 bg-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">×</span>
              </div>
            )}
          </button>
        ))}
      </div>
    );
  };

  const handleToggleStar = async (loanId, currentStatus) => {
    const loanRef = doc(db, 'loanApplications', loanId);
    try {
      await updateDoc(loanRef, {
        starred: !currentStatus
      });
      console.log(`Loan ${loanId} star status updated successfully.`);
      setLoans(prevLoans => prevLoans.filter(l => l.id !== loanId));
    } catch (error) {
      console.error('Error updating star status:', error);
    }
  };

  const renderSection = () => {
    let loansToRender;
    let sectionTitle;

    if (searchTerm) {
       loansToRender = searchResults;
       sectionTitle = "Search Results";
     } else if (selectedLoanType === 'starred') {
       let filteredLoans = loans;

       // Apply status filter
       if (statusFilter) {
         filteredLoans = filteredLoans.filter(loan => getLoanStatus(loan) === statusFilter);
       }

       // Apply stage filter
       if (selectedStage) {
         filteredLoans = filteredLoans.filter(loan => getLoanStage(loan) === selectedStage);
       }

       // Sort the filtered list
       loansToRender = [...filteredLoans].sort((a, b) => {
         const statusA = getLoanStatus(a);
         const statusB = getLoanStatus(b);
         const priority = { urgent: 0, followUpNeeded: 1, onTrack: 2 };
         return priority[statusA] - priority[statusB];
       });


      return (
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Stats Section */}
           {displayStats && (
             <div className="mb-8">
               <h3 className="text-base font-semibold text-gray-900">Current Stats</h3>
               <dl className="mt-5 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-3 md:divide-x md:divide-y-0">
                 {displayStats.map((item) => (
                   <div key={item.name} className="px-4 py-5 sm:p-6">
                     <dt className="text-base font-normal text-gray-900">{item.name}</dt>
                     <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                       <div className="flex items-baseline text-2xl font-semibold text-indigo-600">
                         {item.stat}
                         <span className="ml-2 text-sm font-medium text-gray-500">{item.previousStat}</span>
                       </div>
                     </dd>
                   </div>
                 ))}
               </dl>
             </div>
           )}

          {/* Quick Stats Bar */}
          <QuickStatsBar
            loans={loans}
            onFilterClick={setStatusFilter}
            activeFilter={statusFilter}
          />

          {/* Stage Navigation */}
          <div className="mb-6">
             <div className="bg-white rounded-lg shadow p-4">
               <h4 className="text-sm font-medium text-gray-500 mb-3">Filter by Stage</h4>
               <div className="flex flex-wrap gap-2">
                 {LOAN_STAGES.map((stage) => {
                   const stageCount = loans.filter(loan => getLoanStage(loan) === stage.id).length;
                   return (
                     <button
                       key={stage.id}
                       onClick={() => setSelectedStage(selectedStage === stage.id ? null : stage.id)}
                       className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium
                         ${selectedStage === stage.id
                           ? `bg-${stage.color}-100 text-${stage.color}-800 ring-2 ring-${stage.color}-600`
                           : `bg-${stage.color}-50 text-${stage.color}-600 hover:bg-${stage.color}-100`
                         }`}
                     >
                       {stage.label}
                       <span className={`ml-2 px-2 py-0.5 rounded-full text-xs bg-${stage.color}-100`}>
                         {stageCount}
                       </span>
                     </button>
                   );
                 })}
                 {selectedStage && (
                   <button
                     onClick={() => setSelectedStage(null)}
                     className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium text-gray-500 hover:bg-gray-100"
                   >
                     Clear Filter
                     <span className="ml-1">×</span>
                   </button>
                 )}
               </div>
             </div>
           </div>

          {/* Filter indicators */}
          {(statusFilter || selectedStage) && (
             <div className="mb-4 flex items-center justify-between bg-indigo-50 p-3 rounded-lg">
               <span className="text-indigo-700">
                 Showing {loansToRender.length} {selectedStage ? LOAN_STAGES.find(s => s.id === selectedStage)?.label : ''}
                 {statusFilter ? ` ${statusFilter === 'onTrack' ? 'on-track' : (statusFilter === 'followUpNeeded' ? 'follow-up needed' : statusFilter)}` : ''} applications
               </span>
               <div className="flex gap-2">
                 {statusFilter && (
                   <button
                     onClick={() => setStatusFilter(null)}
                     className="text-indigo-600 hover:text-indigo-800"
                   >
                     Clear status filter
                   </button>
                 )}
                 {selectedStage && (
                   <button
                     onClick={() => setSelectedStage(null)}
                     className="text-indigo-600 hover:text-indigo-800"
                   >
                     Clear stage filter
                   </button>
                 )}
               </div>
             </div>
           )}

          {/* Alphabetical Navigation */}
          <div className="mb-4 sticky top-0 z-10 bg-white shadow rounded-lg p-2">
            <div className="flex justify-center space-x-1">
              {alphabet.map((letter) => {
                const hasLoans = loansToRender.some(loan => getFirstChar(loan) === letter);
                return (
                  <button
                    key={letter}
                    onClick={() => {
                      const element = document.getElementById(`section-${letter}`);
                      if (element) { element.scrollIntoView({ behavior: 'smooth' }); }
                    }}
                    className={`px-2 py-1 text-sm font-medium rounded ${
                      hasLoans
                        ? 'text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500'
                        : 'text-gray-300 cursor-not-allowed'
                    }`}
                    disabled={!hasLoans}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grouped Loans List - Restoring the full card structure */}
          <div className="space-y-8">
            {alphabet.map((letter) => {
              const letterLoans = loansToRender.filter(loan => getFirstChar(loan) === letter);
              if (letterLoans.length === 0) return null;

              return (
                <div key={letter} id={`section-${letter}`} className="relative">
                  <div className="sticky top-20 z-0 bg-gray-100 py-2">
                    <h3 className="text-lg font-semibold text-gray-900 px-4">{letter}</h3>
                  </div>
                  <ul role="list" className="space-y-4 mt-2">
                    {letterLoans.map((loan) => {
                      const loanStatusClass = getLoanStatus(loan);
                      let borderColorClass = 'border-green-500';
                      if (loanStatusClass === 'urgent') {
                          borderColorClass = 'border-red-500';
                      } else if (loanStatusClass === 'followUpNeeded') {
                          borderColorClass = 'border-yellow-500';
                      }
                      const nextTask = getNextTask(loan);

                      return (
                        <li key={loan.id} className={`relative bg-white rounded-lg shadow-sm mb-4 border-2 border-dotted ${borderColorClass}`}>
                          <button
                              onClick={() => handleToggleStar(loan.id, loan.starred)}
                              className="absolute top-3 right-3 text-yellow-400 hover:text-yellow-500 focus:outline-none"
                              title={loan.starred ? "Unstar this application" : "Star this application"}
                             >
                              <StarIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          <div className="flex items-center justify-between gap-x-6 p-5">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start gap-x-3">
                                <p className="text-sm/6 font-semibold text-gray-900">
                                  {loan.businessName || `${loan.firstName} ${loan.lastName}`.trim()}
                                </p>
                                <p
                                  className={classNames(
                                    statuses[loan.loanPurpose === 'Acquisition' ? 'Acquisition' : loan.loanStatus] || 'text-gray-600 bg-gray-50 ring-gray-500/10',
                                    'mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset'
                                  )}
                                >
                                  {loan.loanPurpose === 'Acquisition' ? 'Acquisition' : loan.loanStatus}
                                </p>
                              </div>
                              <div className="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
                                <p className="whitespace-nowrap">
                                  Loan Amount: ${Number(loan.loanSize).toLocaleString()}
                                </p>
                                <svg viewBox="0 0 2 2" className="size-0.5 fill-current">
                                  <circle r={1} cx={1} cy={1} />
                                </svg>
                                <p className="truncate">
                                  Created: {loan.createdAt?.toDate?.().toLocaleDateString() || new Date(loan.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              {loan.notes && (
                                <div className="mt-1 text-xs text-gray-500">
                                  <span className="font-medium">Notes:</span> {loan.notes}
                                </div>
                              )}
                            </div>

                            {/* Next Task Section */}
                            <div className="flex-1 flex justify-center">
                              <div className="flex items-center space-x-4">
                                <span className={`inline-flex items-center rounded-md px-2 py-1 text-sm font-medium ring-1 ring-inset ${nextTask.status}`}>
                                  {nextTask.text}
                                </span>
                                <Link
                                  to={`/loan-details/${loan.id}`}
                                  className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                  View Details
                                </Link>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Floating Action Button */}
          <div className="fixed bottom-6 right-6">
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="p-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      // Original section-based logic
      if (selectedLoanType === 'calls') {
        const compareDate = new Date(selectedDate);
        
        if (activeSection === 'upcomingCalls') {
          loansToRender = loans.filter(loan => {
            const relevantDate = loan.createdAt?.toDate() || new Date(loan.createdAt);
            return !loan.called && 
                   !loan.allDocumentsUploaded && 
                   isToday(relevantDate, compareDate);
          });
          sectionTitle = "Upcoming Calls";
        } else {
          loansToRender = loans.filter(loan => !loan.allDocumentsUploaded);
          sectionTitle = selectedDate.toLocaleDateString() === new Date().toLocaleDateString()
            ? "Today's Applications"
            : `Applications - ${selectedDate.toLocaleDateString()}`;
        }
      } else if (selectedLoanType === 'workingCapital') {
        switch (activeSection) {
          case LoanStatuses.NEW_APPLICATION:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.NEW_APPLICATION);
            sectionTitle = "New Applications";
            break;
          case LoanStatuses.PREQUALIFIED_FOR:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.PREQUALIFIED_FOR);
            sectionTitle = "Prequalified";
            break;
          case LoanStatuses.DOCUMENT_CLARIFICATION:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.DOCUMENT_CLARIFICATION);
            sectionTitle = "Document Review";
            break;
          case LoanStatuses.UNDERWRITING:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.UNDERWRITING);
            sectionTitle = "Underwriting";
            break;
          case LoanStatuses.FUNDED:
            loansToRender = loans.filter(loan => loan.status === LoanStatuses.FUNDED);
            sectionTitle = "Funded";
            break;
          case LoanStatuses.SUBMITTED:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.SUBMITTED);
            sectionTitle = "Submitted Applications";
            break;
          case 'bankManagement':
            return <BankManagement />;
          default:
            loansToRender = recentUsers;
            sectionTitle = "Recent Activity";
        }
      } else {
        switch (activeSection) {
          case 'recentActivity':
            loansToRender = recentUsers;
            sectionTitle = "Recent Activity";
            break;
          case 'preLender':
            loansToRender = preLenderLoans;
            sectionTitle = "Pre-Lender Loans";
            break;
          case 'inTalks':
            loansToRender = inTalksLoans;
            sectionTitle = "In Talks with Lender";
            break;
          case 'closedDeals':
            loansToRender = closedDeals;
            sectionTitle = "Closed Deals";
            break;
          case 'prequalifications':
            return (
              <div className="px-4 sm:px-6 lg:px-8">
                <h2 className="text-2xl font-semibold mb-6">{sectionTitle}</h2>
                <PrequalificationTabDetails
                  prequalifications={prequalifications}
                  handleCardClick={(application) => {
                    navigate(`/admin/prequalification/${application.id}`);
                  }}
                />
              </div>
            );
          case 'bankManagement':
            return <BankManagement />;
          default:
            loansToRender = [];
            sectionTitle = "";
        }
      }
    }

    console.log('Loans to render:', loansToRender);

    if (selectedLoanType === 'lenderMatch') {
      let sectionTitle;
      switch (activeSection) {
        case 'pendingMatches':
          sectionTitle = 'Pending Matches';
          break;
        case 'matched':
          sectionTitle = 'Matched Applications';
          break;
        case 'declined':
          sectionTitle = 'Declined Matches';
          break;
        default:
          sectionTitle = 'All Matches';
      }

      return (
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">{sectionTitle}</h2>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Create Lender Match
            </button>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            {lenderMatches.length === 0 ? (
              <p className="text-gray-500">No matches found.</p>
            ) : (
              lenderMatches.map((match) => (
                <LenderMatchCard
                  key={match.id}
                  {...match}
                  onClick={() => navigate(`/lender-match/${match.id}`)}
                />
              ))
            )}
          </div>

          <CreateLenderMatchModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={() => {
              // Refresh the lender matches
              const fetchLenderMatches = async () => {
                // ... existing fetchLenderMatches code ...
              };
              fetchLenderMatches();
            }}
          />
        </div>
      );
    }

    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">
            {searchTerm ? `Search Results for "${searchTerm}"` : sectionTitle}
          </h2>
          
          {selectedLoanType !== 'starred' && (
            <div className="flex items-center justify-center space-x-4 bg-white rounded-lg shadow p-2">
              <button
                onClick={() => changeDate('prev')}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              
              <span className="text-lg font-medium px-2">{format(selectedDate, 'MMM d, yyyy')}</span>
              
              <button
                onClick={() => changeDate('next')}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          )}

          <div className="flex gap-4">
            <select
              value={selectedLoanType}
              onChange={(e) => setSelectedLoanType(e.target.value)}
              className="block rounded-md border-gray-300 py-1.5 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            >
              {LOAN_TYPE_FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isSearching ? (
          <div className="flex justify-center items-center py-8">
            <p>Searching...</p>
          </div>
        ) : loansToRender?.length === 0 ? (
          <p>{searchTerm ? 'No results found.' : 'No starred applications found.'}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loansToRender.map((loan) => {
              const displayName = loan.businessName || `${loan.firstName} ${loan.lastName}`.trim();
              return (
                <LightModeLoanCard
                  key={loan.id}
                  id={loan.id}
                  name={displayName}
                  loanSize={loan.projectedSalePrice || loan.loanSize}
                  loanPurpose={loan.businessSummary || loan.loanPurpose}
                  businessState={loan.businessState}
                  businessAge={loan.yearsInBusiness}
                  submittedAt={loan.lastUpdated ? new Date(loan.lastUpdated).toLocaleDateString() : (loan.createdAt && loan.createdAt.toDate ? loan.createdAt.toDate().toLocaleDateString() : 'N/A')}
                  ebitda={loan.ebitda}
                  onClick={() => handleCardClick(loan)}
                  isBlue={activeSection === 'prequalifications'}
                  action={loan.action}
                  firstName={loan.firstName}
                  called={loan.followUpDate ? loan.followupCalled : loan.called}
                  personalGuarantors={loan.personalGuarantors}
                  isFollowUp={!!loan.followUpDate}
                  onCallStatusChange={fetchAllLoans}
                  isCallsTab={selectedLoanType === 'calls'}
                  notes={loan.notes}
                  starred={loan.starred || false}
                  loanStatus={loan.loanStatus}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleSaveFinancials = async (loanId, year, financials) => {
    const loanRef = doc(db, 'loanApplications', loanId);
    
    try {
      await updateDoc(loanRef, {
        [`financialAnalysis.${year}`]: financials
      });
      console.log('Financials saved successfully');
    } catch (error) {
      console.error('Error saving financials:', error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchLenderMatches = async () => {
      if (selectedLoanType === 'lenderMatch') {
        try {
          const matchesRef = collection(db, 'lenderMatches');
          let q;
          
          // Filter based on active section
          switch (activeSection) {
            case 'pendingMatches':
              q = query(matchesRef, where('status', '==', 'pending'));
              break;
            case 'matched':
              q = query(matchesRef, where('status', '==', 'matched'));
              break;
            case 'declined':
              q = query(matchesRef, where('status', '==', 'declined'));
              break;
            default:
              q = query(matchesRef);
          }

          const querySnapshot = await getDocs(q);
          const matchesData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setLenderMatches(matchesData);
        } catch (error) {
          console.error('Error fetching lender matches:', error);
          setLenderMatches([]);
        }
      }
    };

    fetchLenderMatches();
  }, [selectedLoanType, activeSection]);

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Loan type toggle buttons */}
          <div className="mb-4">
            <div className="flex justify-end">
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <button
                  type="button"
                  onClick={() => handleLoanTypeChange('Acquisition')}
                  className={`px-4 py-2 text-sm font-medium ${
                    selectedLoanType === 'Acquisition'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border border-gray-200 rounded-l-lg`}
                >
                  Acquisition
                </button>
                <button
                  type="button"
                  onClick={() => handleLoanTypeChange('workingCapital')}
                  className={`px-4 py-2 text-sm font-medium ${
                    selectedLoanType === 'workingCapital'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border-t border-b border-gray-200`}
                >
                  Working Capital
                </button>
                <button
                  type="button"
                  onClick={() => handleLoanTypeChange('prequalification')}
                  className={`px-4 py-2 text-sm font-medium ${
                    selectedLoanType === 'prequalification'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border-t border-b border-gray-200`}
                >
                  Prequalification
                </button>
                <button
                  type="button"
                  onClick={() => handleLoanTypeChange('calls')}
                  className={`px-4 py-2 text-sm font-medium ${
                    selectedLoanType === 'calls'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border border-gray-200`}
                >
                  Calls
                </button>
                <button
                  type="button"
                  onClick={() => handleLoanTypeChange('starred')}
                  className={`px-4 py-2 text-sm font-medium ${
                    selectedLoanType === 'starred'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border border-gray-200`}
                >
                  Starred
                </button>
                <button
                  type="button"
                  onClick={() => handleLoanTypeChange('lenderMatch')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                    selectedLoanType === 'lenderMatch'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border border-gray-200`}
                >
                  Lender Match
                </button>
              </div>
            </div>
          </div>

          {/* Show tabs only for specific views */}
          {(selectedLoanType === 'Acquisition' || selectedLoanType === 'workingCapital' || selectedLoanType === 'prequalification') && (
            <div className="border-b border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between">
              <h3 className="text-base font-semibold leading-6 text-gray-900">
                {selectedLoanType === 'Acquisition'
                  ? 'Acquisition Loans'
                  : selectedLoanType === 'workingCapital'
                    ? 'Working Capital Loans'
                    : 'Prequalification Applications'}
              </h3>
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <div className="hidden sm:block">
                  <nav className="-mb-px flex space-x-8">
                    {getCurrentTabs().map((tab) => (
                      <button
                        key={tab.name}
                        onClick={() => setActiveSection(tab.section)}
                        className={classNames(
                          activeSection === tab.section
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'whitespace-nowrap border-b-2 px-1 pb-4 text-sm font-medium'
                        )}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}
          
          {/* Search and logout section */}
          <div className="mt-3 sm:mt-4">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <div className="mt-2 flex rounded-md shadow-sm">
                  <div className="relative flex-grow focus-within:z-10">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="text"
                      name="search"
                      id="search"
                      className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Search applicants"
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                <button
                  onClick={handleLogout}
                  className="block rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-offset"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main>
        <div className="max-w-7xl mx-auto py-6">
          <div className="sm:px-0">
            {selectedLoanType === 'prequalification' ? (
              <PrequalificationTabDetails
                prequalifications={prequalifications}
                handleCardClick={(application) => {
                  navigate(`/admin/prequalification/${application.id}`);
                }}
              />
            ) : (
              renderSection()
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;

